import {useSelector} from 'react-redux';
import {UseSendProps, ChannelType} from '../../../hooks/sendHooks.types';
import {convertStructureToWhatsappText, prepareParameterValuesForSave} from '../../../component/whatsAppTemplateEditor/hooks/useTemplateStructure';
import setGlobalState from '../../../redux/setGlobalState';
import {TState} from '../../../redux/store.type';
import {saveFreeWhatsAppMessage} from '../../../redux/branding/brandingActions';
import {convertToWhatsappText, serializeTemplateStructure} from '../../whatsAppTemplateEditor/utils/formatUtils';
import {useEffect} from 'react';
import {getUserPreferences} from '../../../redux/user/userActions';
import {dismissLoaderPopupAI} from '../../loaderPopupAI/dismissLoaderPopupAI';
import {setWhatsAppEditorMode} from '../../whatsAppTemplateEditor/hooks/useWhatsAppEditorMode';

export const useSend = ({whatsappData, emailData, smsData, show, sendFunction, saveFunction, data, saveData}: UseSendProps) => {
    const source = saveData?.source as string | undefined;
    const currentSaved = useSelector((s: TState) =>
        source ? s.mailing?.designMailing?.[source] ?? null : null
    );
    const whatsappEditorMode = useSelector((s: TState) => {
        if(!source) return null;
        return s.ui.whatsappEditorMode?.[source];
    });
    const loaderSuppressed = useSelector((s: TState) => Boolean(s.user?.preferences?.hideWhatsappFreeLoader));
    const getUserPreferencesXHR = useSelector((s: TState) => s.user.getUserPreferencesXHR);
    useEffect(() => {
        if(!getUserPreferencesXHR) {
            getUserPreferences().then();
        }
    }, []);

    const handleSend = async (type: ChannelType, trialRecipients?: string[]) => {
        const prepared = whatsappData.whatsappTemplate ? prepareParameterValuesForSave(whatsappData.whatsappTemplate) : [];
        const whatsappPayload = type === 'whatsapp' && whatsappEditorMode === 'free'
            ? {
                ...whatsappData,
                message: convertToWhatsappText(whatsappData.message ?? ''),
                parameterValues: prepared,
            }
            : {...whatsappData, parameterValues: prepared};
        const messageData = {
            whatsapp: whatsappPayload,
            email: emailData,
            sms: smsData,
        };
        try {
            await sendFunction(type, {
                ...messageData[type],
                ...(trialRecipients && {trialRecipients}),
                ...data
            });
            return true;
        } catch (error) {
            console.error('Failed to send:', error);
            show.close();
            return false;
        }
    };

    const handleSave = async (type: ChannelType) => {
        if(!saveFunction) return false;
        if(type === 'email' && !emailData.emailBody) return false;
        if(type === 'sms' && !smsData.message) return false;

        const payload: Record<string, unknown> = {messageType: type, ...saveData};
        const updated = {...(currentSaved ?? {})};

        if(type === 'whatsapp') {
            const hasFreeChanged = whatsappData.message !== serializeTemplateStructure(whatsappData.whatsappTemplate);
            if(whatsappEditorMode === 'free' && hasFreeChanged) {
                const shouldShowLoader = getUserPreferencesXHR === 'SUCCESS' && !loaderSuppressed;
                if(shouldShowLoader) {
                    setGlobalState('ui.show.isOpenAiLoaderPopup', true);
                }
                await saveFreeWhatsAppMessage(whatsappData.message, source, source);
                setWhatsAppEditorMode(source, 'template');
                if(shouldShowLoader) {
                    dismissLoaderPopupAI();
                }
                return true;
            } else if(whatsappData.whatsappTemplate) {
                const message = JSON.stringify(convertStructureToWhatsappText(whatsappData.whatsappTemplate));
                payload.bodyMessage = message;
                updated.whatsappContent = message;
                payload.image = whatsappData.image;
                updated.whatsappImage = whatsappData.image;
            }
        }
        if(type === 'email') {
            payload.bodyMessage = emailData.emailBody;
            payload.subject = emailData.emailSubject;
            payload.image = emailData.image;
            updated.emailContent = emailData.emailBody;
            updated.emailSubject = emailData.emailSubject;
            updated.emailImage = emailData.image;
        } else if(type === 'sms') {
            payload.bodyMessage = smsData.message;
            updated.smsContent = smsData.message;
        }

        await saveFunction(payload);
        if(source) {
            setGlobalState(`mailing.designMailing.${source}`, updated);
        }
        return true;
    };

    return {handleSend, handleSave};
};
